import { useEffect } from 'react';
import { useLocalStorageState } from 'ahooks';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { appRoutes } from '@/constants/routes';
import { DROPPED_FROM_BY_ROUTE_KEY } from '@/constants/navigation';
import { urlOverlays } from '@/constants/url-overlays';
import { hasUrlOverlay } from '@/hooks/use-url-overlay';
import { useRouteInfo } from '@/hooks/use-route-info';
import { AccountMenu } from '@/components/account-menu';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Tooltip } from '@flintmint/web-shared/components/tooltip';

import LogoDark from '@/assets/icons/logo-dark.svg?react';

import styles from './navigation-rail.module.scss';

type NavigationRoute = {
  name: string;
  icon: string;
  url: string;
  testId: string;
  defaultTo?: string; // Fallback when there is no saved dropped-from URL for this section
  captureBackToFrom?: boolean; // When entering from another section, pass backToFrom in location state
};

export const NavigationRail = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [droppedFromMap = {}, setDroppedFromMap] = useLocalStorageState<Record<string, string>>(DROPPED_FROM_BY_ROUTE_KEY, {
    defaultValue: {},
  });

  const { basePath } = useRouteInfo();

  useEffect(() => {
    // Returning to a section never reopens an overlay the user left it with; other params stay as written.
    const params = new URLSearchParams(location.search);
    const overlayParams = Object.values(urlOverlays).filter((overlay) => hasUrlOverlay(params, overlay));
    let search = location.search;
    if (overlayParams.length) {
      overlayParams.forEach(({ param }) => params.delete(param));
      search = params.size ? `?${params}` : '';
    }
    const currentUrl = location.pathname + search;

    if (droppedFromMap[basePath] === currentUrl) {
      return;
    }

    setDroppedFromMap({
      ...droppedFromMap,
      [basePath]: currentUrl,
    });
  }, [basePath, droppedFromMap, location.pathname, location.search, setDroppedFromMap]);

  const topRoutes: NavigationRoute[] = [
    {
      name: 'Home',
      icon: 'home-05',
      url: appRoutes.library,
      testId: 'route-home',
    },
    {
      name: 'Instructions',
      icon: '3rd-bracket',
      url: appRoutes.instructions,
      testId: 'route-instructions',
    },
    {
      name: 'Tags',
      icon: 'tags',
      url: appRoutes.tags,
      testId: 'route-tags',
    },
    {
      name: 'Cortex',
      icon: 'ai-brain-01',
      url: appRoutes.cortex,
      testId: 'route-cortex',
    },
  ];

  const bottomRoutes: NavigationRoute[] = [
    {
      name: 'Archive',
      icon: 'archive-02',
      url: appRoutes.archive,
      testId: 'route-archive',
      captureBackToFrom: true,
    },
    {
      name: 'Settings',
      icon: 'settings-01',
      testId: 'settings',
      url: appRoutes.settings,
      captureBackToFrom: true,
      defaultTo: appRoutes.settingsPersonalization,
    },
  ];

  const renderRoutes = (routes: NavigationRoute[]) => {
    return (
      <div className={styles.routes}>
        {routes.map((route) => {
          // Prefer the last URL visited in this section, then an optional default child, (e.g. settings → personalization), otherwise the section root
          const target = droppedFromMap[route.url] ?? route.defaultTo ?? route.url;

          return (
            <Tooltip placement="right" content={route.name} key={route.url}>
              <NavLink
                aria-label={route.name}
                className={styles.route}
                data-testid={route.testId}
                to={target}
                onClick={(event) => {
                  // Leave modified / non-primary clicks to the browser (new tab, etc.) via `to`
                  if (event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;

                  if (route.captureBackToFrom) {
                    event.preventDefault();
                    // Stash which section we left so BackTo can return there
                    // If we're already in this section, keep existing location.state instead
                    navigate(target, {
                      state: basePath === route.url ? location.state : { backToFrom: basePath },
                    });
                    return;
                  }

                  if (target === route.url) return;

                  event.preventDefault();
                  navigate(target);
                }}
              >
                {({ isActive }) => <Icon type={`${route.icon}${isActive ? '-solid' : ''}`} size={20} />}
              </NavLink>
            </Tooltip>
          );
        })}
      </div>
    );
  };

  return (
    <nav className={styles.navigationRail}>
      <div className={styles.topNav}>
        <NavLink to={appRoutes.library} className={styles.navLogo}>
          <LogoDark />
        </NavLink>
        {renderRoutes(topRoutes)}
      </div>
      <div className={styles.bottomNav}>
        {renderRoutes(bottomRoutes)}
        <AccountMenu />
      </div>
    </nav>
  );
};
